/**
 * NotificationsSlideOut — Notifications panel
 *
 * Fetches real notifications from the API (GET /notifications).
 * Maps the DB schema { channel, event_type, subject, body, read_at, sent_at }
 * to the UI shape { title, message, type, timestamp, read }.
 * Supports marking individual/all notifications as read.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Bell, Trash2, CheckCircle, AlertTriangle, Info, RefreshCw } from 'lucide-react';
import SlideOutPanel from '../common/SlideOutPanel';
import { notificationsApi } from '../../services/api';
import { cn } from '../../lib/utils';

// ── DB → UI shape mapping ─────────────────────────────────────────────────────

interface RawNotification {
  id: string;
  channel: string;
  event_type: string;
  subject: string | null;
  body: string;
  metadata: Record<string, any> | null;
  read_at: string | null;
  sent_at: string | null;
  created_at: string;
}

interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  timestamp: string;
  read: boolean;
}

function deriveType(raw: RawNotification): Notification['type'] {
  const et = raw.event_type ?? '';
  const sev = raw.metadata?.severity ?? '';
  if (sev === 'critical' || et.includes('critical') || et.includes('error')) return 'error';
  if (sev === 'high' || et.includes('anomaly') || et.includes('alert')) return 'warning';
  if (et.includes('completed') || et.includes('verified') || et.includes('provisioned')) return 'success';
  return 'info';
}

function toUiNotification(raw: RawNotification): Notification {
  return {
    id: raw.id,
    title: raw.subject ?? raw.event_type.replace(/\./g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    message: raw.body,
    type: deriveType(raw),
    timestamp: raw.sent_at ?? raw.created_at,
    read: raw.read_at !== null,
  };
}

// ── Sub-components ────────────────────────────────────────────────────────────

function NotificationIcon({ type }: { type: Notification['type'] }) {
  switch (type) {
    case 'success': return <CheckCircle   className="w-5 h-5 text-green-500 flex-shrink-0" />;
    case 'warning': return <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0" />;
    case 'error':   return <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />;
    default:        return <Info          className="w-5 h-5 text-blue-500 flex-shrink-0" />;
  }
}

function notifBg(type: Notification['type']) {
  switch (type) {
    case 'success': return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
    case 'warning': return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800';
    case 'error':   return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
    default:        return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';
  }
}

function formatTime(ts: string) {
  const d = new Date(ts);
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1)   return 'just now';
  if (diffMin < 60)  return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24)    return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7)     return `${diffD}d ago`;
  return d.toLocaleDateString();
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface NotificationsSlideOutProps {
  isOpen: boolean;
  onClose: () => void;
  onUnreadCountChange?: (count: number) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function NotificationsSlideOut({ isOpen, onClose, onUnreadCountChange }: NotificationsSlideOutProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState<string | null>(null);
  // Track whether we have ever fetched so we only load once on mount, not on
  // every route navigation (the component stays mounted inside MainLayout).
  const hasFetched = useRef(false);

  const unreadCount = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);

  // Propagate count to parent (bell badge)
  useEffect(() => {
    onUnreadCountChange?.(unreadCount);
  }, [unreadCount, onUnreadCountChange]);

  // Fetch from API whenever the panel opens
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await notificationsApi.getAll({ limit: 50 });
      const raw: RawNotification[] = res.data ?? [];
      setNotifications(raw.map(toUiNotification));
      hasFetched.current = true;
    } catch {
      setError('Could not load notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load once on first mount (to populate the badge count) — not on every re-mount
  useEffect(() => {
    if (!hasFetched.current) load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload whenever the panel is opened
  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  // Mark single as read (optimistic + API)
  const handleMarkAsRead = async (id: string) => {
    setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
    try { await notificationsApi.markRead(id); } catch { /* ignore */ }
  };

  // Mark all as read (optimistic + API — fire-and-forget each)
  const handleMarkAllAsRead = () => {
    const unread = notifications.filter((n) => !n.read);
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    unread.forEach((n) => notificationsApi.markRead(n.id).catch(() => {}));
  };

  // Remove from local list only (no delete endpoint currently)
  const handleDelete = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  return (
    <SlideOutPanel isOpen={isOpen} onClose={onClose} title="Notifications" width="medium">
      <div className="space-y-4">

        {/* Header row */}
        <div className="flex items-center justify-between pb-4 border-b border-gray-200 dark:border-metallic-700">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {loading
              ? 'Loading…'
              : `${unreadCount} unread ${unreadCount === 1 ? 'notification' : 'notifications'}`}
          </p>
          <div className="flex items-center gap-3">
            {unreadCount > 0 && !loading && (
              <button
                onClick={handleMarkAllAsRead}
                className="text-xs text-primary-600 dark:text-primary-400 hover:underline font-medium"
              >
                Mark all as read
              </button>
            )}
            <button
              onClick={load}
              disabled={loading}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-40"
              title="Refresh"
            >
              <RefreshCw className={cn('w-3.5 h-3.5', loading && 'animate-spin')} />
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <p className="text-xs text-red-500 text-center">{error}</p>
        )}

        {/* List */}
        <div className="space-y-3">
          {!loading && notifications.length === 0 ? (
            <div className="text-center py-8">
              <Bell className="w-12 h-12 text-gray-300 dark:text-gray-700 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">No notifications</p>
            </div>
          ) : (
            notifications.map((n) => (
              <div
                key={n.id}
                className={cn(
                  'p-3 rounded-lg border transition-colors hover:shadow-sm',
                  n.read
                    ? 'bg-white dark:bg-metallic-900 border-gray-200 dark:border-metallic-800'
                    : notifBg(n.type)
                )}
              >
                <div className="flex gap-3">
                  <div className="pt-0.5"><NotificationIcon type={n.type} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="font-medium text-sm text-gray-900 dark:text-white leading-snug">
                        {n.title}
                      </h4>
                      {!n.read && (
                        <span className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-1.5" />
                      )}
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                      {n.message}
                    </p>
                    <div className="flex items-center justify-between gap-2 mt-2">
                      <span className="text-xs text-gray-400">{formatTime(n.timestamp)}</span>
                      <div className="flex items-center gap-2">
                        {!n.read && (
                          <button
                            onClick={() => handleMarkAsRead(n.id)}
                            className="text-xs text-primary-600 dark:text-primary-400 hover:underline leading-none"
                          >
                            Mark read
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(n.id)}
                          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                          title="Dismiss"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </SlideOutPanel>
  );
}

export default NotificationsSlideOut;

// Made with Bob
